import type { ModelCapabilityFlags, ModelCapabilityModalityKey } from '@/types/model-capabilities';

/** 模型订阅详情 / 列表接口最小结构（L2 headless 与 pages 共用，禁止 headless import @/pages） */
export interface ModelSubscriptionDetail {
  id?: string;
  name?: string;
  aliase?: string;
  domain?: string;
  pathRule?: string;
  path?: string;
  ignoreCase?: boolean;
  provider?: string;
  consumer?: string;
  consumers?: string[];
  usageMethod?: string;
  metadata?: {
    name?: string;
    creationTimestamp?: string;
    labels?: Record<string, string>;
    annotations?: Record<string, string>;
  };
  creator?: string;
  status?: {
    customModelID?: string;
  };
  spec?: {
    alias?: string;
    allowedClientCIDRs?: string[];
    allowedClientIPs?: string[];
    creator?: string;
    description?: string;
    strategyName?: string;
    workspace?: string;
    rateLimits?: ModelSubscriptionRateLimits;
    resourceLimits?: ModelSubscriptionResourceLimits;
    domain?: string;
    path?: string;
    models?: { name: string; category: ModelCapabilityModalityKey[] }[];
    llmgwStrategyRef?: {
      external?: {
        strategyName?: string;
        customModelID?: string;
      };
      inline?: {
        name?: string;
        modalities?: ModelCapabilityFlags;
      };
    };
    targetServices?: {
      providerName?: string;
      weight?: number;
      modelMapping?: Record<string, string>;
    }[];
  };
  strategy?: {
    name?: string;
    modalities?: ModelCapabilityFlags;
  };
}

export interface ModelSubscriptionRateLimits {
  inputTokens?: { enabled?: boolean; value?: number; windowSeconds?: number };
  outputTokens?: { enabled?: boolean; value?: number; windowSeconds?: number };
}

export interface ModelSubscriptionResourceLimits {
  billingTotalTokens?: { enabled?: boolean; value?: number };
  inputTokens?: { enabled?: boolean; value?: number };
  outputTokens?: { enabled?: boolean; value?: number };
  requests?: { enabled?: boolean; value?: number };
}

/** MS 高级设置表单开关（与 MsSpec.switches 对齐） */
export interface ModelSubscriptionFormSwitches {
  showTokenLimit: boolean;
  showRequests: boolean;
  showInputTokens: boolean;
  showOutputTokens: boolean;
}

/** 表单是否显式携带 switches（未显式时应走配额推断） */
export function hasExplicitModelSubscriptionSwitchFields(values: Record<string, unknown>): boolean {
  const nested = values.switches as Partial<ModelSubscriptionFormSwitches> | undefined;
  if (!nested || typeof values.switches !== 'object') return false;
  return (
    nested.showTokenLimit !== undefined ||
    nested.showRequests !== undefined ||
    nested.showInputTokens !== undefined ||
    nested.showOutputTokens !== undefined
  );
}

export function readModelSubscriptionFormSwitches(values: Record<string, unknown>): ModelSubscriptionFormSwitches {
  const nested = (values.switches as Partial<ModelSubscriptionFormSwitches> | undefined) ?? {};
  return {
    showTokenLimit: Boolean(nested.showTokenLimit),
    showRequests: Boolean(nested.showRequests),
    showInputTokens: Boolean(nested.showInputTokens),
    showOutputTokens: Boolean(nested.showOutputTokens),
  };
}

/** 写入表单：switches 为唯一真源 */
export function modelSubscriptionFormSwitchesToFields(switches: ModelSubscriptionFormSwitches): Record<string, unknown> {
  return {
    switches: { ...switches },
  };
}
